import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { requiredValidator } from "components/Form/Form.utils";
import { Callout } from "components/Form/components/Callout/Callout";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { Form } from "components/Form/components/Form/Form";
import { PasswordFormComponent } from "components/Form/components/Password/PasswordFormComponent";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import SimpleNotification from "components/Notification/SimpleNotification";
import { BlockingNavigationModal } from "components/Prompt/BlockingNavigationModal";
import { getUserData } from "containers/Auth/Auth.selectors";
import { webhookSecurityPath } from "containers/Auth/constants";
import {
    AuthMethodsContainer,
    defaultAuthValue,
} from "containers/Auth/screens/AuthMethodsContainer/AuthMethodsContainer";
import { TwoFaCodeFieldIfEnabled } from "containers/Auth/sharedFields";
import { formId } from "containers/Resources/screens/ResourcesSingle/ResourcesSingle";
import { FormApi } from "final-form";
import { Webhook } from "graphql/generated/graphql";
import { Field, Form as FinalForm } from "react-final-form";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import strings from "utils/strings";
import "./WebhookSecurity.scss";
import { WebhookSecurityInnerForm } from "./WebhookSecurityInnerForm";

interface WebhookSecurityPopoverProps {
    closePopover: () => void;
    fetchResourceData?: () => void;
    resourceData: Webhook;
    isReadOnly: boolean;
}

const getDirtyValues = (formData, form: FormApi) => {
    const { initialValues, dirtyFields } = form.getState();

    const result = {};
    ["is_enabled", "require_login"].forEach((field) => {
        if (dirtyFields[field]) {
            result[`new_${field}`] = formData[field];
        }
    });
    if (
        formData.require_key_field.is_key_required !==
            initialValues.require_key_field.is_key_required ||
        formData.require_key_field.key !== initialValues.require_key_field.key
    ) {
        result["new_key"] = formData.require_key_field.key;
    }
    return result;
};

export const WebhookSecurityPopover = ({
    closePopover,
    fetchResourceData,
    resourceData,
    isReadOnly,
}: WebhookSecurityPopoverProps) => {
    const history = useHistory();

    const userData = useSelector(getUserData);

    const onSubmit = (formData: any, form) => {
        const dirtyValues = getDirtyValues(formData, form);

        // return submit errors
        if (
            formData.require_key_field.is_key_required === true &&
            formData.require_key_field.key.length === 0 &&
            dirtyValues["new_key"] !== undefined
        ) {
            return { require_key_field: "Key is required." };
        }
        restClient
            .post(webhookSecurityPath, {
                user_id: userData.identity_id,
                password: formData.password,
                twofa_code: formData.twofaCode,
                webhook_id: resourceData.id,
                authentication_method: formData.authenticationMethod ?? defaultAuthValue,
                ...dirtyValues,
            })
            .then(() => {
                NotificationService.success(<SimpleNotification title="Webhook was configured." />);
                document.getElementById(formId)?.dispatchEvent(
                    new Event("submit", {
                        bubbles: true,
                        cancelable: true,
                    })
                );
            })
            .then(() => {
                fetchResourceData?.();
            })
            .catch((error) => {
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="There was a problem configuring Webhook"
                        error={error}
                    />
                );
            })
            .finally(() => {
                closePopover();
            });
    };

    return (
        <div>
            <div className="header">Edit webhook security</div>
            <FinalForm
                onSubmit={onSubmit}
                initialValues={{
                    is_enabled: resourceData.is_enabled,
                    require_key_field: { is_key_required: resourceData.is_key_required, key: "" },
                    require_login: resourceData.require_login,
                }}
                keepDirtyOnReinitialize
            >
                {({ handleSubmit, hasValidationErrors, values, form, pristine }) => {
                    const dirtyFields = getDirtyValues(values, form);
                    const isFormPristine = Object.keys(dirtyFields).length === 0;

                    return (
                        <Form onSubmit={handleSubmit}>
                            <BlockingNavigationModal
                                navigate={history.push}
                                shouldBlockNavigation={!pristine}
                                titleText={strings.navigateAwayUnsavedPrompt}
                                submitForm={hasValidationErrors ? undefined : handleSubmit}
                            />
                            <WebhookSecurityInnerForm isReadOnly={isReadOnly} />
                            {values.require_login === false &&
                            values.require_key_field.is_key_required === false ? (
                                <div className="notificationContainer">
                                    <Callout
                                        text="WARNING: You are about to create an unprotected webhook. Please set a key or require login token."
                                        type={"warning"}
                                    />
                                </div>
                            ) : null}
                            {values.require_key_field.is_key_required === true ? (
                                <div className="notificationContainer">
                                    <Callout
                                        type="info"
                                        text={
                                            <>
                                                <strong>Providing the key — choose one of:</strong>
                                                <ul
                                                    style={{
                                                        margin: "4px 0 0 0",
                                                        paddingLeft: "20px",
                                                    }}
                                                >
                                                    <li>
                                                        <strong>Bearer token</strong> (recommended):{" "}
                                                        <code>
                                                            Authorization: Bearer &lt;key&gt;
                                                        </code>
                                                    </li>
                                                    <li>
                                                        <strong>JSON body</strong>: add{" "}
                                                        <code>"key": "&lt;key&gt;"</code> to the
                                                        JSON request body — only works if the body
                                                        is a JSON object and <code>key</code> does
                                                        not conflict with your payload fields.
                                                    </li>
                                                    <li>
                                                        <strong>Query parameter</strong>{" "}
                                                        (discouraged):{" "}
                                                        <code>?key=&lt;key&gt;</code> — URL
                                                        parameters may appear in server logs.
                                                    </li>
                                                </ul>
                                            </>
                                        }
                                    />
                                </div>
                            ) : null}
                            {values.require_login === true ? (
                                <div className="notificationContainer">
                                    <Callout
                                        type="info"
                                        text={
                                            <>
                                                <strong>Providing the login token:</strong> When
                                                calling this webhook from a browser where you are
                                                already logged in to Cloudomation, the session
                                                cookie is sent automatically. For other clients,
                                                first obtain a session cookie by logging in, then
                                                include it in the request as an HTTP header:{" "}
                                                <code>Cookie: session=&lt;value&gt;</code>
                                            </>
                                        }
                                    />
                                </div>
                            ) : null}
                            <AuthMethodsContainer />
                            <Form.Row>
                                <p className="user-auth-modal__note">
                                    Please enter your current password.
                                </p>
                                <Field
                                    name="password"
                                    id="password"
                                    fieldProps={{
                                        label: "Password*",
                                        clearable: false,
                                    }}
                                    component={PasswordFormComponent}
                                    validate={requiredValidator}
                                />
                            </Form.Row>
                            <TwoFaCodeFieldIfEnabled />
                            <Form.Row className="sign-in--offset">
                                <CancelButton onClick={() => closePopover()} />
                                <Button
                                    submit
                                    label="OK"
                                    disabled={isFormPristine || hasValidationErrors}
                                />
                            </Form.Row>
                        </Form>
                    );
                }}
            </FinalForm>
        </div>
    );
};
